import { isEmpty } from "psims/lib/empty";
import { recordActionFromEnum } from "psims/models/api/data-submission-record-action";
import { EnforceableArrangement } from "../types";

const CHAR_LIMIT = 200;

const ENTITY_NAME_REGEX = /^[a-zA-Z0-9\-'()& ]+$/;

// Allows integers and decimals with up to 2 decimal places
const DECIMAL_2DP_REGEX = /^\d+(\.\d{1,2})?$/;

// Start and end date on the arrangement only become required when the obligation date is after this cutoff.
const START_END_DATE_REQUIRED_AFTER = new Date('2026-06-29T00:00:00');

function hasInvalidCharacters(value: string | undefined | null): boolean {
  if (!value) return false;
  return !ENTITY_NAME_REGEX.test(value);
}

function hasInvalidDecimal(value: number | string | undefined | null): boolean {
  if (value == null || value === '') return false;
  return !DECIMAL_2DP_REGEX.test(String(value));
}


export type StorageValidationField =
  | 'ownerName'
  | 'beneficiary'
  | 'msoProductId'
  | 'stockOwnershipAgreementType'
  | 'volume'
  | 'obligationDate'
  | 'startDateOfThisArrangement'
  | 'endDateOfThisArrangement';

export type StorageValidationResult = {
  key: StorageValidationField;
} & (
  | { code: 'invalid_required' }
  | { code: 'character_limit'; limit: number }
  | {code:  'invalid_volume'}
  | { code: 'invalid_end_date'}
  | { code: 'invalid_start_date_after_obligation' }  
  | { code: 'invalid_end_date_before_obligation' }   
  | { code: 'invalid_characters' }
  | { code: 'invalid_decimal'; decimalPlaces: number }
);

export function validateStorage(
  storage: EnforceableArrangement,
): Array<StorageValidationResult> {
  const messages: Array<StorageValidationResult> = [];

  // Skip validation for rows marked for deletion
  if (recordActionFromEnum(storage.recordAction) === 'Delete') {
    return messages;
  }

  if (isEmpty(storage.ownerName)) {
    messages.push({ key: 'ownerName', code: 'invalid_required' });
  } else if ((storage.ownerName?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'ownerName', code: 'character_limit', limit: CHAR_LIMIT });
  } else if (hasInvalidCharacters(storage.ownerName)) {
    messages.push({ key: 'ownerName', code: 'invalid_characters' });
  }

  if (isEmpty(storage.beneficiary)) {
    messages.push({ key: 'beneficiary', code: 'invalid_required' });
  } else if ((storage.beneficiary?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'beneficiary', code: 'character_limit', limit: CHAR_LIMIT });
  } else if (hasInvalidCharacters(storage.beneficiary)) {
    messages.push({ key: 'beneficiary', code: 'invalid_characters' });
  }

  if (isEmpty(storage.msoProductId)) {
    messages.push({ key: 'msoProductId', code: 'invalid_required' });
  }

  if (isEmpty(storage.stockOwnershipAgreementType)) {
    messages.push({ key: 'stockOwnershipAgreementType', code: 'invalid_required' });
  }

  if (isEmpty(storage.volume)) {
    messages.push({ key: 'volume', code: 'invalid_required' });
  } else if (
    storage.volume == null || !Number.isInteger(storage.volume) || storage.volume < 0 || storage.volume > 1_000_000
  ) {
    messages.push({ key: 'volume', code: 'invalid_volume' });
  } else if (hasInvalidDecimal(storage.volume)) {
    messages.push({ key: 'volume', code: 'invalid_decimal', decimalPlaces: 2 });
  }

  if (isEmpty(storage.obligationDate)) {
    messages.push({ key: 'obligationDate', code: 'invalid_required' });
  }

  // Start/end date are only required once the obligation date is after the cutoff
  const obligationDateExceedsThreshold =
    !isEmpty(storage.obligationDate) &&
    new Date(storage.obligationDate!) > START_END_DATE_REQUIRED_AFTER;

  if (obligationDateExceedsThreshold) {
    if (isEmpty(storage.startDateOfThisArrangement)) {
      messages.push({ key: 'startDateOfThisArrangement', code: 'invalid_required' });
    }

    if (isEmpty(storage.endDateOfThisArrangement)) {
      messages.push({ key: 'endDateOfThisArrangement', code: 'invalid_required' });
    }
  }

  if (
    !isEmpty(storage.startDateOfThisArrangement) &&
    !isEmpty(storage.obligationDate) &&
    new Date(storage.startDateOfThisArrangement!) > new Date(storage.obligationDate!)
  ) {
    messages.push({ key: 'startDateOfThisArrangement', code: 'invalid_start_date_after_obligation' });
  }

  // End date must be >= Obligation date
  if (
    !isEmpty(storage.endDateOfThisArrangement) &&
    !isEmpty(storage.obligationDate) &&
    new Date(storage.endDateOfThisArrangement!) < new Date(storage.obligationDate!)
  ) {
    messages.push({ key: 'endDateOfThisArrangement', code: 'invalid_end_date_before_obligation' });
  }

  // End date must be >= Start date
  if (
    !isEmpty(storage.startDateOfThisArrangement) &&
    !isEmpty(storage.endDateOfThisArrangement) &&
    new Date(storage.endDateOfThisArrangement!) < new Date(storage.startDateOfThisArrangement!)
  ) {
    messages.push({ key: 'endDateOfThisArrangement', code: 'invalid_end_date' });
  }

  return messages;
}
